import { makeStyles } from "@fluentui/react-components";
import {
  AirplaneLandingFilled,
  AirplaneTakeOffFilled,
} from "@fluentui/react-icons";
import * as d3 from "d3";
import { $cloud100, $sky60 } from "../../../../../cssjs/colors";
import { filledColorSelection } from "../../../../../utils/common/CommonHelperFunctions";

interface Slice {
  label: string;
  value: number;
  color: string;
}

interface SemiDonutProps {
  percentage: number;
  width: number; // overall width of SVG
  height: number; // overall height of SVG
  outerRadius: number; // outer radius of the donut
  innerRadius: number; // inner radius (for hollow center)
  data: any;
  fillColor?: string;
  emptyColor?: string;
  cornerRadius?: number;
}
export const KPIHalfDonut: React.FunctionComponent<SemiDonutProps> = ({
  percentage,
  width,
  height,
  outerRadius,
  innerRadius,
  data,
  fillColor = "#0078D4",
  emptyColor = "#C8C6C4",
}: SemiDonutProps): React.JSX.Element => {
  const styles = useStyles();

  const pie = d3
    .pie<Slice>()
    .value((d: any) => d.value)
    .sort(null)
    .startAngle(-Math.PI / 2) // start at 12 o’clock
    .endAngle(Math.PI / 2); // end at 6 o’clock
  const value = Math.max(0, Math.min(percentage, 100));
  // Add a small gap between filled and empty by inserting a white arc, but skip if 100% or 0%
  const gapValue = value >= 100 || value <= 0 ? 0 : value === 99 ? 1 : 2; // degrees of gap (can adjust for more/less space)
  let DonutData = [
    {
      label: "filled",
      value: value,
      color: filledColorSelection(data?.status) ?? fillColor,
    },
    {
      label: "gap",
      value: gapValue,
      color: "#fff",
    },
    {
      label: "empty",
      value: Math.max(0, 100 - value - gapValue),
      color: emptyColor,
    },
  ];
  const arc = d3
    .arc<d3.PieArcDatum<Slice>>()
    .innerRadius(innerRadius)
    .outerRadius(outerRadius);

  const arcs = pie(DonutData);

  return (
    <div className="mt-1 pt-2" style={{ textAlign: "center" }}>
      <div
        className={`${styles.fontcolor} mb-1 d-flex`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div className="fs-18" style={{ marginRight: 8 }}>
          {data?.type !== "Overall" &&
            (data?.type === "Arrivals" ? (
              <AirplaneLandingFilled />
            ) : (
              <AirplaneTakeOffFilled />
            ))}
        </div>
        <div className="fw-500">{data?.type}</div>
      </div>
      <svg
        width={140}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{
          boxSizing: "content-box",
          overflow: "visible",
        }}
        focusable={false}
      >
        <g transform={`translate(${width / 2}, ${height})`}>
          {arcs.map((d, i) => (
            <path
              key={i}
              d={
                d3
                  .arc<d3.PieArcDatum<Slice>>()
                  .innerRadius(outerRadius - 23) // Reduce thickness by increasing innerRadius
                  .outerRadius(outerRadius)(d)!
              }
              fill={d.data.color}
            />
          ))}

          <text
            textAnchor="middle"
            alignmentBaseline="middle"
            fontSize="40px"
            fontWeight="600"
            fill={$cloud100}
            y={-outerRadius / 6}
          >
            {value}%
          </text>
        </g>
      </svg>
    </div>
  );
};

const useStyles = makeStyles({
  fontcolor: {
    color: $sky60,
  },
});
